import { Table as AntTable, TableProps as AntTableProps, theme } from 'antd';
import styled from 'styled-components';
import type { GlobalToken } from 'antd';

export type TableSize = 'small' | 'middle' | 'large';

export interface TableColumn<T> {
  /** Title of the column */
  title: React.ReactNode;
  /** Key of the data field */
  dataIndex?: keyof T | string;
  /** Unique key for the column */
  key?: string;
  /** Width of the column */
  width?: string | number;
  /** Whether the column is sortable */
  sortable?: boolean;
  /** Sort function */
  sorter?: (a: T, b: T) => number;
  /** Sort directions */
  sortDirections?: ('ascend' | 'descend')[];
  /** Default sort order */
  defaultSortOrder?: 'ascend' | 'descend';
  /** Whether the column is filterable */
  filterable?: boolean;
  /** Filter options */
  filters?: { text: string; value: string }[];
  /** Filter function */
  onFilter?: (value: string | number | boolean, record: T) => boolean;
  /** Current filtered value */
  filteredValue?: (string | number | boolean)[] | null;
  /** Fixed position */
  fixed?: 'left' | 'right';
  /** Alignment of the column */
  align?: 'left' | 'center' | 'right';
  /** Render function */
  render?: (value: unknown, record: T, index: number) => React.ReactNode;
  /** Whether column is hidden */
  hidden?: boolean;
}

export interface TableProps<T extends object> extends Omit<
  AntTableProps<T>,
  'columns' | 'size'
> {
  /** Table data */
  readonly data: T[];
  /** Table columns configuration */
  readonly columns: TableColumn<T>[];
  /** Loading state */
  readonly loading?: boolean;
  /** Row key */
  readonly rowKey?: keyof T | ((record: T) => string);
  /** Table size */
  readonly size?: TableSize;
  /** Pagination config (false to disable) */
  readonly pagination?: AntTableProps<T>['pagination'];
  /** Row click handler */
  readonly onRowClick?: (record: T) => void;
  /** Row selection config */
  readonly rowSelection?: AntTableProps<T>['rowSelection'];
  /** Whether to show table border */
  readonly bordered?: boolean;
  /** Scroll config */
  readonly scroll?: AntTableProps<T>['scroll'];
  /** Additional class name */
  readonly className?: string;
  /** Test id */
  readonly 'data-testid'?: string;
}

interface TableWrapperProps {
  $token: GlobalToken;
}

const TableWrapper = styled.div<TableWrapperProps>`
  .ant-table {
    font-family: ${({ $token }) => $token.fontFamily};
  }

  .ant-table-thead > tr > th {
    background: ${({ $token }) => $token.colorBgLayout};
    color: ${({ $token }) => $token.colorText};
    font-weight: ${({ $token }) => $token.fontWeightStrong};
    font-size: ${({ $token }) => $token.fontSizeSM}px;
    border-bottom: ${({ $token }) =>
      `${$token.lineWidth}px solid ${$token.colorBorderSecondary}`};
    padding: ${({ $token }) => `${$token.paddingSM}px ${$token.padding}px`};

    &::before {
      display: none;
    }
  }

  .ant-table-tbody > tr > td {
    color: ${({ $token }) => $token.colorText};
    font-size: ${({ $token }) => $token.fontSizeSM}px;
    border-bottom: ${({ $token }) =>
      `${$token.lineWidth}px solid ${$token.colorBorderSecondary}`};
    padding: ${({ $token }) => `${$token.paddingSM}px ${$token.padding}px`};
  }

  .ant-table-tbody > tr:hover > td {
    background: ${({ $token }) => $token.colorBgLayout};
  }

  .ant-table-tbody > tr.ant-table-row-selected > td {
    background: ${({ $token }) => $token.colorPrimaryBg};
  }

  /* Pagination styling */
  .ant-pagination {
    padding: ${({ $token }) => $token.padding}px;
    margin: 0;

    .ant-pagination-item {
      border-radius: ${({ $token }) => $token.borderRadius}px;
      border-color: ${({ $token }) => $token.colorBorderSecondary};
      margin-inline-end: ${({ $token }) => $token.marginXS}px;

      &:hover {
        border-color: ${({ $token }) => $token.colorPrimary};
      }

      &-active {
        background: ${({ $token }) => $token.colorPrimary};
        border-color: ${({ $token }) => $token.colorPrimary};

        a {
          color: ${({ $token }) => $token.colorWhite};
        }
      }
    }

    .ant-pagination-prev,
    .ant-pagination-next {
      margin-inline-end: ${({ $token }) => $token.marginXS}px;

      button {
        border-radius: ${({ $token }) => $token.borderRadius}px;
        border-color: ${({ $token }) => $token.colorBorderSecondary};

        &:hover {
          border-color: ${({ $token }) => $token.colorPrimary};
          color: ${({ $token }) => $token.colorPrimary};
        }
      }
    }
  }

  /* Sorter styling */
  .ant-table-column-sorter {
    color: ${({ $token }) => $token.colorTextDisabled};
  }

  .ant-table-column-sorter-up.active,
  .ant-table-column-sorter-down.active {
    color: ${({ $token }) => $token.colorPrimary};
  }

  /* Empty state */
  .ant-table-placeholder {
    .ant-empty-description {
      color: ${({ $token }) => $token.colorTextTertiary};
    }
  }

  /* Loading */
  .ant-spin-dot-item {
    background-color: ${({ $token }) => $token.colorPrimary};
  }

  /* Row clickable */
  &.table-clickable .ant-table-tbody > tr {
    cursor: pointer;
  }

  /* Remove sorted column background */
  .ant-table-column-sort:not(.ant-table-cell-row-hover) {
    background: transparent;
  }

  /* First and last cell padding */
  .ant-table-tbody,
  .ant-table-thead {
    td:first-child,
    th:first-child {
      padding-left: ${({ $token }) => $token.paddingLG}px;
    }

    td:last-child,
    th:last-child {
      padding-right: ${({ $token }) => $token.paddingLG}px;
    }
  }
`;

/**
 * Table component based on Libertify Design System
 *
 * Now uses Ant Design theme tokens for consistent styling.
 *
 * @example
 * ```tsx
 * import { Table, type TableColumn } from '@libertify/ui';
 *
 * interface Project {
 *   id: string;
 *   name: string;
 *   status: string;
 * }
 *
 * const columns: TableColumn<Project>[] = [
 *   { title: 'Name', dataIndex: 'name', sortable: true },
 *   { title: 'Status', dataIndex: 'status' },
 * ];
 *
 * <Table
 *   data={projects}
 *   columns={columns}
 *   rowKey="id"
 *   onRowClick={(record) => navigate(`/projects/${record.id}`)}
 * />
 * ```
 */
export function Table<T extends object>({
  data,
  columns,
  loading = false,
  rowKey = 'id' as keyof T,
  size = 'middle',
  pagination = { pageSize: 20 },
  onRowClick,
  rowSelection,
  bordered = false,
  scroll,
  className,
  'data-testid': testId,
  ...props
}: TableProps<T>) {
  const { token } = theme.useToken();

  // Convert our column format to antd format
  const antdColumns = columns
    .filter((col) => !col.hidden)
    .map((col) => ({
      title: col.title,
      dataIndex: col.dataIndex as string,
      key: col.key ?? (col.dataIndex as string),
      width: col.width,
      sorter: col.sortable ? col.sorter : undefined,
      sortDirections: col.sortDirections,
      defaultSortOrder: col.defaultSortOrder,
      filters: col.filters,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      onFilter: col.onFilter as any,
      filteredValue: col.filteredValue,
      fixed: col.fixed,
      align: col.align,
      render: col.render,
    }));

  const rowKeyFn =
    typeof rowKey === 'function'
      ? rowKey
      : (record: T) => record[rowKey] as string;

  return (
    <TableWrapper
      $token={token}
      className={onRowClick ? 'table-clickable' : undefined}
    >
      <AntTable<T>
        dataSource={data}
        columns={antdColumns}
        loading={loading}
        rowKey={rowKeyFn}
        size={size}
        pagination={pagination}
        rowSelection={rowSelection}
        bordered={bordered}
        scroll={scroll}
        className={className}
        data-testid={testId}
        onRow={
          onRowClick
            ? (record) => ({
                onClick: () => onRowClick(record),
              })
            : undefined
        }
        {...props}
      />
    </TableWrapper>
  );
}

export default Table;
